import type { BriefComment } from '@dyspatch/core';
import { useState } from 'react';
import { Markdown } from '@/components/common/Markdown';
import { Button } from '@/components/ui/button';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';

/**
 * One agent finding, wherever a person reads it (docs/JOB-LIFECYCLE.md 2.2).
 *
 * A finding used to be drawn twice: once by the task pane, which knew it was a
 * finding, and once by the job page's comments region, which only knew it was a
 * comment with a task key beside it. The two readings drifted (the job page's
 * had no verb at all), because there were two places that had to agree on what
 * a finding looks like. This is the one place now: the marker that says an
 * agent wrote it, the task it came from, the body, and the verb or the issue
 * link once one exists.
 *
 * The task chip is drawn even on the task pane, where it repeats what the page
 * around it already says. That repetition is the point: the same component
 * renders the same finding the same way everywhere it appears, rather than a
 * pane deciding for itself which parts of a finding it has room for. It is also
 * what a test can pin - a finding drawn twice from identical props is
 * byte-identical output, or it is not the same component.
 *
 * The body is read as the markdown the agent wrote it as (issue #70): a fence
 * stays a fence and a path stays code, at a measure this component sets rather
 * than the wrap width of whatever terminal wrote the finding.
 */
export function Finding({ note }: { note: BriefComment }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="bg-raised text-fg-2 rounded-sm px-1 font-mono text-2xs">
          finding · {note.author}
        </span>
        {note.taskKey ? (
          <span className="bg-raised text-fg-2 rounded-sm px-1 font-mono text-2xs">
            {note.taskKey}
          </span>
        ) : null}
      </div>
      {/* 88ch, the measure the job's own goal is set at, rather than the 65ch
          `prose` default. A finding is read on the job page in a column over a
          thousand pixels wide, and 65ch there is a thin ribbon of text down the
          left of an empty card. In the 400px task pane the container binds
          first either way, so one measure serves both and the two renderings
          stay byte-identical. */}
      <Markdown source={note.body} className="mt-1 max-w-[88ch] text-sm" />
      <FindingIssue note={note} />
    </div>
  );
}

/**
 * The finding's one verb. Outward-facing, so it asks in place first
 * (docs/JOB-LIFECYCLE.md's rule for anything that leaves the hub); once filed
 * the row shows the issue instead, and the verb does not come back - refiling
 * returns the same issue rather than opening a second one, both because the
 * hub route is idempotent and because there is nothing left here to press.
 */
function FindingIssue({ note }: { note: BriefComment }) {
  const [asking, setAsking] = useState(false);
  const taskId = note.taskId;
  const file = useHubAction(
    () => {
      // Every agent finding is written against the task that produced it
      // (`insertAgentNotes` sets `taskId` from the row it read); a missing one
      // would mean this note predates task ids and was never a finding, which
      // `note.authorKind === 'agent'` already rules out. The check stays so a
      // future caller that hands this a shaped-but-wrong note fails with a
      // sentence rather than a silent no-op.
      if (!taskId) throw new Error('this finding has no task to file the issue against');
      return api.fileFindingAsIssue(taskId, note.id);
    },
    {
      onDone: () => setAsking(false),
      success: 'Filed on GitHub.',
      inlineError: true,
    },
  );

  if (note.issueUrl) {
    return (
      <p className="mt-1 text-2xs">
        Filed as{' '}
        <a href={note.issueUrl} target="_blank" rel="noreferrer" className="underline">
          {issueLabel(note.issueUrl)}
        </a>
      </p>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {asking ? (
        <>
          <span className="text-fg-2 text-2xs">Open a labelled issue on GitHub?</span>
          <Button size="sm" variant="outline" disabled={file.busy} onClick={() => file.run()}>
            {file.busy ? 'Filing…' : 'File'}
          </Button>
          <Button size="sm" variant="ghost" disabled={file.busy} onClick={() => setAsking(false)}>
            Keep here
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAsking(true)}>
          File as issue
        </Button>
      )}
      {file.error ? (
        <span className="text-bad text-2xs" role="alert">
          {file.error}
        </span>
      ) : null}
    </div>
  );
}

/** `owner/repo#12` for a GitHub issue link, else the URL itself. */
function issueLabel(url: string): string {
  const match = /github\.com\/([^/]+\/[^/]+)\/issues\/(\d+)/.exec(url);
  return match ? `${match[1]}#${match[2]}` : url;
}
